import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  IconButton,
  InputAdornment,
  Box,
  Fade,
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import CloseIcon from '@material-ui/icons/Close';
import ProfileRegistrationService from '../dashboard/Locality/Service/profileRegistrationService';
import useStyles from './styles';

const steps = ['Enter Email', 'Verify OTP', 'Reset Password'];

export default function ForgotPasswordDialog({ open, onClose }) {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleClose = () => {
    // Reset all state
    setActiveStep(0);
    setEmail('');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setOtpSent(false);
    setResendTimer(0);
    setLoading(false);
    onClose();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendOTP = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await ProfileRegistrationService.sendPasswordResetOTP({ email });
      
      // Always show success message without exposing OTP
      // In development mode, OTP is logged to server console only
      if (response?.developmentMode) {
        // In development mode, just indicate email was attempted
        setSuccess(`If the email exists, an OTP has been sent to ${email}. Please check your inbox (and spam folder).`);
      } else {
        setSuccess(`If the email exists, an OTP has been sent to ${email}. Please check your inbox (and spam folder).`);
      }
      
      setOtpSent(true);
      setActiveStep(1);
      // Start resend timer (60 seconds)
      setResendTimer(60);
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      
      // Check if it's an email configuration error
      if (errorMsg.includes('Email server') || errorMsg.includes('ECONNREFUSED') || errorMsg.includes('email configuration')) {
        setError('Email service is currently unavailable. Please contact support or try again later.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    await handleSendOTP();
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim() || otp.trim().length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await ProfileRegistrationService.verifyPasswordResetOTP({ email, otp });
      if (response.isValid) {
        setSuccess('OTP verified successfully');
        setActiveStep(2);
      } else {
        setError(response.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await ProfileRegistrationService.resetPasswordWithOTP({
        email,
        otp,
        newPassword,
        confirmPassword,
      });
      setSuccess(response.message || 'Password reset successfully!');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
              Enter your registered email address. We'll send you a One-Time Password (OTP) to reset your password.
            </Typography>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              margin="normal"
              variant="outlined"
              disabled={loading}
              error={!!error && !email.trim()}
              helperText={error && !email.trim() ? error : ''}
              autoFocus
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
              We've sent a 6-digit OTP to <strong>{email}</strong>. Please check your inbox (and spam folder) and enter the code below.
            </Typography>
            <TextField
              fullWidth
              label="OTP"
              type="text"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);
                setError('');
              }}
              margin="normal"
              variant="outlined"
              disabled={loading}
              error={!!error}
              helperText={error || 'Enter the 6-digit code from your email'}
              inputProps={{
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' },
              }}
              autoFocus
            />
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Didn't receive the OTP?
              </Typography>
              <Button
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || loading}
                color="primary"
                size="small"
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
              Enter your new password. Make sure it's at least 6 characters long.
            </Typography>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError('');
              }}
              margin="normal"
              variant="outlined"
              disabled={loading}
              error={!!error && (!newPassword.trim() || newPassword.length < 6)}
              helperText={
                error && (!newPassword.trim() || newPassword.length < 6)
                  ? error
                  : 'Minimum 6 characters'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
              }}
              margin="normal"
              variant="outlined"
              disabled={loading}
              error={!!error && (newPassword !== confirmPassword)}
              helperText={
                error && newPassword !== confirmPassword
                  ? error
                  : 'Re-enter your new password'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      handleSendOTP();
    } else if (activeStep === 1) {
      handleVerifyOTP();
    } else if (activeStep === 2) {
      handleResetPassword();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
      setSuccess('');
      if (activeStep === 2) {
        setOtp('');
      }
    }
  };

  const getNextButtonText = () => {
    if (activeStep === 0) return 'Send OTP';
    if (activeStep === 1) return 'Verify OTP';
    if (activeStep === 2) return 'Reset Password';
    return 'Next';
  };

  const isNextDisabled = () => {
    if (loading) return true;
    if (activeStep === 0) return !email.trim() || !validateEmail(email);
    if (activeStep === 1) return !otp.trim() || otp.trim().length !== 6;
    if (activeStep === 2) return !newPassword.trim() || newPassword.length < 6 || newPassword !== confirmPassword;
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 8,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Forgot Password</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel style={{ marginBottom: 30, marginTop: 10 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Fade in={!!error}>
          <Typography color="error" variant="body2" style={{ marginBottom: 10, minHeight: 20 }}>
            {error}
          </Typography>
        </Fade>

        <Fade in={!!success}>
          <Typography color="primary" variant="body2" style={{ marginBottom: 10, minHeight: 20 }}>
            {success}
          </Typography>
        </Fade>

        {renderStepContent()}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} disabled={loading}>
          {activeStep === 0 ? 'Cancel' : 'Close'}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          disabled={isNextDisabled()}
          style={{ backgroundColor: 'rgb(112, 57, 177)' }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : getNextButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

